import { useNavigate } from "react-router-dom";
import { Calendar, Globe, UserCheck, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteContent } from "@/context/SiteContentContext";

const Landing = () => {
  const navigate = useNavigate();
  const { getContent } = useSiteContent();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Globe className="w-8 h-8 text-slate-900" />
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Book an Interpreter</h1>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="header-login-btn"
              variant="ghost"
              onClick={() => navigate("/login")}
              className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            >
              Log In
            </Button>
            <Button
              data-testid="header-register-btn"
              onClick={() => navigate("/register")}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-6 shadow-sm hover:shadow-md transition-all"
            >
              {getContent("landing", "cta_button", "Get Started")}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section px-6 py-20">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6" style={{ fontFamily: 'Manrope' }}>
              {getContent("landing", "hero_title", "Connect with Professional Interpreters")}
            </h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed" style={{ fontFamily: 'Public Sans' }}>
              {getContent("landing", "hero_subtitle", "Schedule interpretation services seamlessly. Browse interpreter availability, book sessions, and manage everything from one professional platform.")}
            </p>
            <div className="flex gap-4">
              <Button
                data-testid="hero-get-started-btn"
                onClick={() => navigate("/register")}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-8 py-6 text-lg font-semibold shadow-sm hover:shadow-md transition-all"
              >
                {getContent("landing", "cta_button", "Get Started")}
              </Button>
              <Button
                data-testid="hero-learn-more-btn"
                variant="outline"
                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                className="border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md px-8 py-6 text-lg font-medium"
              >
                Learn More
              </Button>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1758518731468-98e90ffd7430?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBkaXZlcnNlJTIwZ3JvdXAlMjBvZmZpY2V8ZW58MHx8fHwxNzcyNjQ3NDkwfDA&ixlib=rb-4.1.0&q=85"
              alt="Professional team collaborating"
              className="rounded-lg shadow-xl w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="px-6 py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-4xl font-bold text-slate-900 text-center mb-12" style={{ fontFamily: 'Manrope' }}>
            {getContent("landing", "how_it_works_title", "How It Works")}
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card p-8">
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center mb-4">
                <UserCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
                {getContent("landing", "step_1_title", "Search")}
              </h4>
              <p className="text-slate-600" style={{ fontFamily: 'Public Sans' }}>
                {getContent("landing", "step_1_desc", "Find interpreters by language and specialization")}
              </p>
            </div>
            <div className="card p-8">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
                {getContent("landing", "step_2_title", "Book")}
              </h4>
              <p className="text-slate-600" style={{ fontFamily: 'Public Sans' }}>
                {getContent("landing", "step_2_desc", "Select a time that works for you")}
              </p>
            </div>
            <div className="card p-8">
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center mb-4">
                <Briefcase className="w-6 h-6 text-amber-600" />
              </div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
                {getContent("landing", "step_3_title", "Connect")}
              </h4>
              <p className="text-slate-600" style={{ fontFamily: 'Public Sans' }}>
                {getContent("landing", "step_3_desc", "Meet your interpreter in person, by video, or phone")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope' }}>
            Ready to Get Started?
          </h3>
          <p className="text-xl text-slate-300 mb-8" style={{ fontFamily: 'Public Sans' }}>
            Join our platform today and experience seamless interpreter scheduling.
          </p>
          <Button
            data-testid="cta-register-btn"
            onClick={() => navigate("/register")}
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-md px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
          >
            Create Your Account
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-slate-600">
          <p style={{ fontFamily: 'Public Sans' }}>© 2026 Book an Interpreter. Professional scheduling made simple.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;